import { jsPDF } from 'jspdf';
import { getPage, getPictogram } from '../api/endpoints';
import { getFitzgeraldColor } from '../config/categories';
import { SOURCES } from '../config/pictogramSources';
import { fitTextToCell, wrapTextWithIndices } from './textFieldLayout';
import { segmentText } from './textColorRuns';
import { TEXT_FIELD_FONTS, DEFAULT_TEXT_COLOR, type TextFieldFontFamily } from '../config/textFieldFonts';
import type { Pictogram, SlotAssignment } from '../types';

/* ─── Shared helpers ─── */

const DEFAULT_FOOTER_COUNT = 4;

function gridLayout(count: number) {
  if (count <= 3) return { rows: 1, columns: count };
  if (count <= 6) return { rows: 2, columns: Math.ceil(count / 2) };
  if (count <= 12) return { rows: 2, columns: Math.ceil(count / 2) };
  if (count <= 18) return { rows: 3, columns: Math.ceil(count / 3) };
  return { rows: 4, columns: Math.ceil(count / 4) };
}

function mainSlotIds(count: number) {
  return Array.from({ length: count }, (_, i) => `main_${String(i + 1).padStart(2, '0')}`);
}
function footerSlotIds(count = DEFAULT_FOOTER_COUNT) {
  return Array.from({ length: count }, (_, i) => `footer_${String(i + 1).padStart(2, '0')}`);
}

async function toDataUrl(url: string): Promise<string | null> {
  try {
    const r = await fetch(url, { mode: 'cors' });
    const blob = await r.blob();
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch { return null; }
}

type LoadedFonts = Map<string, { regular: string; bold?: string }>;  // key = jspdf-Name

async function loadFontsForAssignments(assignments: SlotAssignment[]): Promise<LoadedFonts> {
  const needed = new Set<TextFieldFontFamily>();
  for (const a of assignments) {
    if (a.type === 'text' && a.text) needed.add(a.textStyle?.fontFamily ?? 'standard');
  }
  const result: LoadedFonts = new Map();
  await Promise.all([...needed].map(async (key) => {
    const font = TEXT_FIELD_FONTS[key] ?? TEXT_FIELD_FONTS.standard;
    if (!font.files) return;                         // jsPDF built-in
    const regular = await fetchFontB64(font.files.regular);
    if (!regular) return;                            // Fallback: helvetica (drawCell)
    const bold = font.files.bold ? await fetchFontB64(font.files.bold) : null;
    result.set(font.jspdf, { regular, bold: bold ?? undefined });
  }));
  return result;
}

function hexToRgb(hex: string): [number, number, number] {
  let h = (hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(n)) return [40, 40, 40];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

async function fetchFontB64(url: string): Promise<string | null> {
  try {
    const buf = await (await fetch(url)).arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  } catch { return null; }
}


/* ─── Input type for the core renderer ─── */

export interface PdfPageInput {
  title: string;
  slotCount: number;
  showTitle: boolean;
  showFooter: boolean;
  pictogramScale: number;
  footerPictogramScale: number;
  labelScale: number;
  footerLabelScale: number;
  mainAssignments: SlotAssignment[];
  footerAssignments: SlotAssignment[];
  footerCount: number;
  titlePictogramLeft?: SlotAssignment;
  titlePictogramRight?: SlotAssignment;
}

/* ─── Core PDF renderer (single source of truth) ─── */

function renderPdf(
  input: PdfPageInput,
  picsById: Map<string, Pictogram>,
  imgCache: Record<string, string>,
  fonts?: LoadedFonts,
): void {
  const {
    title, slotCount: sc, showTitle, showFooter,
    pictogramScale: pScale, footerPictogramScale: fpScale,
    labelScale: lScale, footerLabelScale: flScale,
    mainAssignments, footerAssignments, footerCount,
    titlePictogramLeft, titlePictogramRight,
  } = input;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  if (fonts) {
    for (const [name, f] of fonts) {
      pdf.addFileToVFS(`${name}-Regular.ttf`, f.regular);
      pdf.addFont(`${name}-Regular.ttf`, name, 'normal');
      if (f.bold) {
        pdf.addFileToVFS(`${name}-Bold.ttf`, f.bold);
        pdf.addFont(`${name}-Bold.ttf`, name, 'bold');
      }
    }
  }
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const mx = 40, my = 30;
  const { rows, columns: cols } = gridLayout(sc);

  let gridTop = my + 12;
  if (showTitle) {
    const titleImgSize = 36;
    const titleBaseY = my + 22;

    if (titlePictogramLeft?.pictogramId && imgCache[titlePictogramLeft.pictogramId]) {
      pdf.addImage(imgCache[titlePictogramLeft.pictogramId]!, mx + 4, my + 2, titleImgSize, titleImgSize);
      if (titlePictogramLeft.label) {
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(titlePictogramLeft.label, mx + 4 + titleImgSize / 2, my + 2 + titleImgSize + 8, { align: 'center' });
      }
    }

    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(title || 'Seite', W / 2, titleBaseY, { align: 'center' });

    if (titlePictogramRight?.pictogramId && imgCache[titlePictogramRight.pictogramId]) {
      pdf.addImage(imgCache[titlePictogramRight.pictogramId]!, W - mx - titleImgSize - 4, my + 2, titleImgSize, titleImgSize);
      if (titlePictogramRight.label) {
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(titlePictogramRight.label, W - mx - titleImgSize / 2 - 4, my + 2 + titleImgSize + 8, { align: 'center' });
      }
    }
    gridTop = my + 48;
  }

  const footerH = showFooter ? 72 : 0;
  const sep = showFooter ? 12 : 0;
  const modellingH = 16;
  const gridBot = H - my - footerH - sep - modellingH;
  const cellW = (W - mx * 2) / cols;
  const availH = gridBot - gridTop;
  const rowH = availH / rows;

  const singleCentered = sc === 1 && !showTitle && !showFooter;
  const cellSize = singleCentered ? Math.min(availH * 0.8, (W - mx * 2) * 0.4) : 0;

  const pdfScale = pScale / 100;
  const pdfFooterScale = fpScale / 100;
  const pdfLabelScale = lScale / 100;
  const pdfFooterLabelScale = flScale / 100;

  const drawCell = (x: number, y: number, w: number, h: number, a: SlotAssignment | undefined, small: boolean) => {
    if (a?.type === 'text' && a.text) {
      const style = a.textStyle;
      const font = TEXT_FIELD_FONTS[style?.fontFamily ?? 'standard'] ?? TEXT_FIELD_FONTS.standard;
      const bold = (style?.bold ?? false) && font.hasBold;
      if (style?.border !== false) {
        pdf.setDrawColor(200);
        pdf.setLineWidth(0.75);
        pdf.roundedRect(x + 3, y + 3, w - 6, h - 6, 5, 5);
      }
      const innerW = w - 16, innerH = h - 12;
      const size = fitTextToCell({
        text: a.text, baseFontSize: style?.fontSize ?? 14,
        cellWidth: innerW, cellHeight: innerH,
        widthFactor: font.widthFactor, bold,
      });
      // Font mit Dateien, aber Laden fehlgeschlagen → helvetica-Fallback
      const fontName = font.files && !fonts?.has(font.jspdf) ? 'helvetica' : font.jspdf;
      pdf.setFont(fontName, bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      const measure = (s: string) => pdf.getTextWidth(s);
      const lines = wrapTextWithIndices(a.text, innerW, measure);
      const baseColor = style?.color ?? DEFAULT_TEXT_COLOR;
      const runs = a.colorRuns ?? [];
      const lineH = size * 1.3;
      const totalH = lines.length * lineH;
      let ty = y + (h - totalH) / 2 + size;              // erste Baseline, vertikal zentriert
      const align = style?.align ?? 'center';
      for (const line of lines) {
        if (ty > y + h - 4) break;                        // Überlauf-Schutz (clip)
        const lineText = a.text.slice(line.start, line.end);
        // Runs auf das Zeilenfenster schneiden und auf Zeilen-Indizes verschieben
        const lineRuns = runs
          .map((r) => ({ start: Math.max(r.start, line.start) - line.start, end: Math.min(r.end, line.end) - line.start, color: r.color }))
          .filter((r) => r.start < r.end);
        const segs = segmentText(lineText, baseColor, lineRuns);
        const lineWidth = measure(lineText);
        let sx = align === 'left' ? x + 8 : align === 'right' ? x + w - 8 - lineWidth : x + (w - lineWidth) / 2;
        for (const seg of segs) {
          const [rr, gg, bb] = hexToRgb(seg.color);
          pdf.setTextColor(rr, gg, bb);
          pdf.text(seg.text, sx, ty);
          sx += measure(seg.text);
        }
        ty += lineH;
      }
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      return;
    }
    const cellPic = a?.pictogramId ? picsById.get(a.pictogramId) : undefined;
    const fColor = cellPic ? getFitzgeraldColor(cellPic).pdfColor : null;
    if (fColor) {
      pdf.setDrawColor(fColor[0], fColor[1], fColor[2]);
      pdf.setLineWidth(2);
    } else {
      pdf.setDrawColor(200);
      pdf.setLineWidth(0.75);
    }
    pdf.roundedRect(x + 3, y + 3, w - 6, h - 6, 5, 5);

    const cellScale = small ? pdfFooterScale : pdfScale;
    if (a?.pictogramId && imgCache[a.pictogramId]) {
      const maxImgSz = Math.min(w - 24, h - (small ? 22 : 30));
      const imgSz = Math.min(maxImgSz * cellScale, maxImgSz);
      const ix = x + (w - imgSz) / 2;
      const iy = y + (h - imgSz - (small ? 14 : 20)) / 2;
      pdf.addImage(imgCache[a.pictogramId]!, ix, iy, imgSz, imgSz);
    }
    if (a?.label) {
      const baseFontSize = small ? 7 : 9;
      const cellLabelScale = small ? pdfFooterLabelScale : pdfLabelScale;
      pdf.setFontSize(Math.max(baseFontSize * cellLabelScale, 4));
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(a.label, x + w / 2, y + h - (small ? 6 : 10), { align: 'center' });
    }
  };

  // Main grid
  const mSlots = mainSlotIds(sc);
  if (singleCentered) {
    const cx = (W - cellSize) / 2;
    const cy = gridTop + (availH - cellSize) / 2;
    drawCell(cx, cy, cellSize, cellSize, mainAssignments.find(a => a.slotId === mSlots[0]), false);
  } else {
    for (let i = 0; i < mSlots.length; i++) {
      const r = Math.floor(i / cols), c = i % cols;
      drawCell(mx + c * cellW, gridTop + r * rowH, cellW, rowH, mainAssignments.find(a => a.slotId === mSlots[i]), false);
    }
  }

  // Footer
  if (showFooter) {
    const footerTop = gridBot + sep;
    pdf.setDrawColor(160);
    pdf.setLineWidth(1.5);
    pdf.line(mx, footerTop - 4, W - mx, footerTop - 4);

    const fSlots = footerSlotIds(footerCount);
    const footerCellW = (W - mx * 2) / footerCount;
    for (let i = 0; i < fSlots.length; i++) {
      drawCell(mx + i * footerCellW, footerTop, footerCellW, 72, footerAssignments.find(a => a.slotId === fSlots[i]), true);
    }
  }

  // Modelling tip
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(140, 140, 140);
  pdf.text(
    'UK-Tipp: Zeige selbst auf die Symbole (Modelling) – so lernt die Person den kommunikativen Wert der Bilder kennen.',
    W / 2, H - my + 4, { align: 'center' }
  );

  // Branding
  pdf.setFontSize(5.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(170, 170, 170);
  pdf.text('Erstellt mit Hilfe von symbolbuch.de', mx, H - 8);

  // Copyright
  const visibleMainSlotIdSet = new Set(mainSlotIds(sc));
  const visibleMain = mainAssignments.filter(a => visibleMainSlotIdSet.has(a.slotId));
  const visibleFooter = showFooter ? footerAssignments.filter(a => footerSlotIds(footerCount).includes(a.slotId)) : [];
  const usedPicIds = new Set(
    [...visibleMain, ...visibleFooter].filter(a => a.pictogramId).map(a => a.pictogramId)
  );
  if (showTitle && titlePictogramLeft?.pictogramId) usedPicIds.add(titlePictogramLeft.pictogramId);
  if (showTitle && titlePictogramRight?.pictogramId) usedPicIds.add(titlePictogramRight.pictogramId);
  const copyrightTexts: string[] = [];
  for (const id of usedPicIds) {
    const pic = picsById.get(id);
    if (!pic) continue;
    const srcCfg = SOURCES.find(s => s.value === pic.source);
    const attr = srcCfg?.defaultAttribution || '';
    if (attr && !copyrightTexts.includes(attr)) copyrightTexts.push(attr);
  }
  const copyrightLine = copyrightTexts.length > 0
    ? copyrightTexts.join(' | ')
    : '\u00A9 Die Nutzungsrechte der verwendeten Symbole liegen bei der erstellenden Person bzw. Einrichtung.';
  pdf.text(copyrightLine, W - mx, H - 8, { align: 'right' });

  pdf.setTextColor(0, 0, 0);
  pdf.save(`${title || 'Seite'}.pdf`);
}

/* ─── Public API ─── */

/**
 * Fetch a page from the API and generate a PDF entirely client-side.
 * Used by the pages list (no editor state available).
 */
export async function generatePagePdf(pageId: string): Promise<boolean> {
  try {
    const page = await getPage(pageId);
    const pScale = page.pictogramScale ?? 100;
    const input: PdfPageInput = {
      title: page.title || 'Seite',
      slotCount: page.slotCount || page.mainAssignments.length || 12,
      showTitle: page.showTitle ?? true,
      showFooter: page.showFooter ?? true,
      pictogramScale: pScale,
      footerPictogramScale: page.footerPictogramScale ?? pScale,
      labelScale: page.labelScale ?? 100,
      footerLabelScale: page.footerLabelScale ?? 100,
      mainAssignments: page.mainAssignments,
      footerAssignments: page.footerAssignments,
      footerCount: page.footerAssignments.length || DEFAULT_FOOTER_COUNT,
      titlePictogramLeft: page.titlePictogramLeft,
      titlePictogramRight: page.titlePictogramRight,
    };

    // Collect referenced pictogram IDs
    const picIds = new Set<string>();
    for (const a of [...input.mainAssignments, ...input.footerAssignments]) {
      if (a.pictogramId) picIds.add(a.pictogramId);
    }
    if (input.titlePictogramLeft?.pictogramId) picIds.add(input.titlePictogramLeft.pictogramId);
    if (input.titlePictogramRight?.pictogramId) picIds.add(input.titlePictogramRight.pictogramId);

    // Fetch pictogram metadata
    const picEntries = await Promise.all(
      Array.from(picIds).map(async (id) => {
        try { return [id, await getPictogram(id)] as [string, Pictogram]; }
        catch { return null; }
      })
    );
    const picsById = new Map(picEntries.filter(Boolean) as [string, Pictogram][]);

    // Fetch images
    const imgCache: Record<string, string> = {};
    await Promise.all(
      Array.from(picsById.entries()).map(async ([id, pic]) => {
        const du = await toDataUrl(`${import.meta.env.VITE_ASSETS_URL}/${pic.previewKey || pic.fileKey}`);
        if (du) imgCache[id] = du;
      })
    );

    const fonts = await loadFontsForAssignments(input.mainAssignments);
    renderPdf(input, picsById, imgCache, fonts);
    return true;
  } catch (err) {
    console.error('PDF export failed:', err);
    return false;
  }
}

/**
 * Generate a PDF from already-loaded editor state.
 * Used by EditorPage where page data and pictograms are already in memory.
 */
export async function generatePdfFromState(
  input: PdfPageInput,
  allPictograms: Pictogram[],
): Promise<void> {
  // Build picsById map from the provided array
  const picIds = new Set<string>();
  for (const a of [...input.mainAssignments, ...input.footerAssignments]) {
    if (a.pictogramId) picIds.add(a.pictogramId);
  }
  if (input.titlePictogramLeft?.pictogramId) picIds.add(input.titlePictogramLeft.pictogramId);
  if (input.titlePictogramRight?.pictogramId) picIds.add(input.titlePictogramRight.pictogramId);

  const picsById = new Map<string, Pictogram>();
  for (const id of picIds) {
    const pic = allPictograms.find(p => p.id === id);
    if (pic) picsById.set(id, pic);
  }

  // Fetch images (use _localThumbnail if available for locally-uploaded pics)
  const imgCache: Record<string, string> = {};
  await Promise.all(
    Array.from(picsById.entries()).map(async ([id, pic]) => {
      const localThumb = (pic as any)._localThumbnail;
      if (localThumb) {
        imgCache[id] = localThumb;
      } else {
        const du = await toDataUrl(`${import.meta.env.VITE_ASSETS_URL}/${pic.previewKey || pic.fileKey}`);
        if (du) imgCache[id] = du;
      }
    })
  );

  const fonts = await loadFontsForAssignments(input.mainAssignments);
  renderPdf(input, picsById, imgCache, fonts);
}
